/**
 * UseWithEditor — template parameter editor for ::use blocks using content templates.
 */

import type {
    PluginDefinition,
    TemplateInputSchema,
    UseBinding,
} from "@magic-md/core";
import { OutputSerializer } from "@magic-md/core";
import { useCallback, useMemo } from "react";
import { useDocumentAssetReferences } from "../hooks/useDocumentAssetReferences";
import { attachDefinitionOutputs, buildConfigPanelUnits } from "../lib/config-panel-units";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { collectScopeVariables } from "../lib/scope-variables";
import {
    resolveSerializerTargetLocator,
    type EditorSerializerTargetLocator,
} from "../lib/unit-edit-target";
import { useEditorStore } from "../store/editor-store";
import { usePaneContext } from "./PaneContext";
import { UseWithParamsEditor } from "./UseWithParamsEditor";

const outputSerializer = new OutputSerializer();

export interface UseWithEditorProps {
    /** The ::use binding from parsedDocument.uses */
    useBinding: UseBinding;
    /** Template parameter names extracted from the content body ($ref{input/xxx}) */
    templateParams: string[];
    /** Optional declared schema for the source template inputs. */
    inputSchema?: TemplateInputSchema;
    /** All available definitions in the document (for $ref autocomplete) */
    definitions: Map<string, PluginDefinition>;
    /** Visual treatment. `plain` is used inside already-scoped template popovers. */
    variant?: "section" | "plain";
    /** Exact source occurrence when opened from a CodeMirror unit widget. */
    locator?: EditorSerializerTargetLocator;
}

export function UseWithEditor({
    useBinding,
    templateParams,
    inputSchema,
    definitions,
    variant = "section",
    locator,
}: UseWithEditorProps) {
    const paneCtx = usePaneContext();
    const contentDefinitions = paneCtx.contentDefinitions;
    const importDocuments = paneCtx.importDocuments;
    const parsedDocument = paneCtx.parsedDocument;
    const definitionsWithOutputs = useMemo(
        () => attachDefinitionOutputs(definitions, paneCtx.definitionOutputs),
        [definitions, paneCtx.definitionOutputs],
    );
    const availableUnits = useMemo(
        () => buildConfigPanelUnits({
            definitions: definitionsWithOutputs,
            definitionOutputs: paneCtx.definitionOutputs,
            forDefinitions: paneCtx.forDefinitions,
            contentDefinitions,
            imports: parsedDocument?.imports,
            uses: collectParsedDocumentUseBindings(parsedDocument),
            forExecutions: paneCtx.forExecutions,
            contentExecutions: paneCtx.contentExecutions,
            importExecutions: paneCtx.importExecutions,
            useExecutions: paneCtx.useExecutions,
        }),
        [
            definitionsWithOutputs,
            paneCtx.definitionOutputs,
            paneCtx.forDefinitions,
            contentDefinitions,
            parsedDocument,
            paneCtx.forExecutions,
            paneCtx.contentExecutions,
            paneCtx.importExecutions,
            paneCtx.useExecutions,
        ],
    );
    const scopeVariables = useMemo(
        () =>
            collectScopeVariables(
                useBinding.id,
                parsedDocument?.blocks ?? [],
                parsedDocument?.forDefinitions,
                definitions,
                { uses: collectParsedDocumentUseBindings(parsedDocument), contentDefinitions },
            ),
        [useBinding.id, parsedDocument, definitions, contentDefinitions],
    );
    const availableAssets = useDocumentAssetReferences(paneCtx.tab?.filePath, importDocuments);
    const refContext = useMemo(() => ({
        currentDefinitionId: useBinding.id,
        getAvailableDefinitions: () => definitionsWithOutputs,
        getAvailableContentDefinitions: () => contentDefinitions,
        getAvailableImportDocuments: () => importDocuments,
        getAvailableAssets: () => availableAssets,
        getAvailableUnits: () => availableUnits,
        scopeVariables,
    }), [
        useBinding.id,
        definitionsWithOutputs,
        contentDefinitions,
        importDocuments,
        availableAssets,
        availableUnits,
        scopeVariables,
    ]);
    const targetKey = [
        useBinding.id,
        locator?.startLine ?? "",
        locator?.endLine ?? "",
        locator?.occurrence ?? "",
        locator?.fingerprint ?? "",
        locator?.candidateCount ?? "",
        useBinding.range.start,
        useBinding.range.end,
    ].join(":");

    const applyWith = useCallback((withObj: Record<string, unknown>) => {
        const editorState = useEditorStore.getState();
        const activeTab = editorState.getActiveTab();
        if (!activeTab) return;

        const bind = useBinding.bind ?? {};
        const patch = useBinding.patch ?? {};
        const targetLocator = resolveSerializerTargetLocator({
            content: activeTab.content,
            unitKind: "use",
            unitId: useBinding.id,
            locator,
            sourceRange: useBinding.range,
        });
        if (locator?.fingerprint && !targetLocator) return;
        const updated = outputSerializer.updateUseConfig(
            activeTab.content,
            useBinding.id,
            patch,
            {
                bind: Object.keys(bind).length > 0 ? bind : undefined,
                with: Object.keys(withObj).length > 0 ? withObj : undefined,
            },
            targetLocator,
        );

        if (updated !== activeTab.content) {
            editorState.setContentImmediate(updated, activeTab.id);
        }
    }, [useBinding.id, useBinding.bind, useBinding.patch, useBinding.range, locator]);

    return (
        <UseWithParamsEditor
            useBinding={useBinding}
            templateParams={templateParams}
            inputSchema={inputSchema}
            context={refContext}
            onApplyWith={applyWith}
            variant={variant}
            targetKey={targetKey}
        />
    );
}
